/* eslint-disable no-console */
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, map, Observable, retry, Subject, take, takeUntil, timer } from 'rxjs';

import { InstructionResponse } from '@efficiently/models';

import { AppConfigService } from '@efficiently/client-core-services';

import { AuthClient } from '@ds/core/enums/auth-client.enum';

import { AI_ASSISTANT_CONFIG } from './ai-assistant.config';
import {
	AiAssistantConfig,
	ConnectionStatus,
	RealtimeSessionResponse,
	ResponseCreateEvent,
	SessionUpdateEvent,
} from './ai-assistant.types';
import { AiToolsControlService } from './ai-tools-control.service';
import { ConnectionManager } from './connection.manager';
import { EventManager } from './event.manager';
import { MemoryManager } from './memory.manager';
import { CREATE_REALTIME_SESSION, GET_INSTRUCTIONS } from './queries/ai-assistant.queries';

@Injectable({
	providedIn: 'root',
})
export class AiAssistantService implements OnDestroy {
	private readonly apollo = inject(Apollo);
	private readonly control = inject(AiToolsControlService);
	private readonly appConfigService = inject(AppConfigService);

	private readonly config: AiAssistantConfig = inject(AI_ASSISTANT_CONFIG);

	private readonly connectionManager: ConnectionManager;
	private readonly eventManager: EventManager;
	private readonly memoryManager: MemoryManager;

	private readonly destroy$ = new Subject<void>();
	private readonly connectionStatus$ = new BehaviorSubject<ConnectionStatus>('disconnected');
	private instructions: InstructionResponse;
	private instructionsAdded: InstructionResponse = new InstructionResponse();

	constructor() {
		this.memoryManager = new MemoryManager(this.config);
		this.connectionManager = new ConnectionManager(this.config);
		this.eventManager = new EventManager(this.control, this.memoryManager, (instruction: string) =>
			this.addInstruction(instruction),
		);
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.disconnect();
	}

	public async initializeVoiceConnection(reinit: boolean = false): Promise<boolean> {
		try {
			this.connectionStatus$.next('connecting');

			const sessionData = await this.createSession();
			this.checkInstructions();

			await this.connectionManager.initialize();
			await this.connectionManager.establishConnection(sessionData);

			const dataChannel = this.connectionManager.getDataChannel();
			if (!dataChannel) {
				throw new Error('Data channel not available');
			}

			this.eventManager.setDataChannel(dataChannel);
			await this.initializeSession(reinit);

			this.connectionStatus$.next('connected');
			return true;
		} catch (error) {
			console.error('Failed to initialize voice connection:', error);
			await this.handleConnectionError(error);
			return false;
		}
	}

	public disconnect(): void {
		this.connectionManager.cleanup();
		this.eventManager.cleanup();
		this.instructionsAdded = new InstructionResponse();
		this.connectionStatus$.next('disconnected');
	}

	public clearMemories(): void {
		this.memoryManager.clearMemories();
	}

	private async createSession(): Promise<RealtimeSessionResponse> {
		return this.createRealtimeSession()
			.pipe(retry(this.config.retryAttempts), takeUntil(this.destroy$))
			.toPromise();
	}

	private async initializeSession(reinit: boolean = false): Promise<void> {
		return new Promise<void>((resolve): void => {
			this.eventManager.addSessionHandler(() => {
				this.setMemorySession();
				this.eventManager.sendData({
					type: 'session.update',
					session: {
						input_audio_transcription: { model: 'whisper-1', language: 'en' },
						turn_detection: { type: 'server_vad', create_response: true },
					},
				});

				if (!reinit) {
					const user = this.appConfigService.config?.user;
					const userName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`;
					this.getResponse(
						`Introduce yourself, tell your name (AI Assistent Design Efficiently) and greet the user by the name ${userName} and offer help.`,
					);
				}

				this.eventManager.addMainEventHandler();

				resolve();
			});
		});
	}

	private async handleConnectionError(error: unknown): Promise<void> {
		console.error('Connection failed:', error);
		this.connectionStatus$.next('error');

		this.disconnect();

		await timer(this.config.retryDelay).toPromise();
	}

	private setMemorySession(): void {
		if (this.memoryManager.hasMemories()) {
			const memories = this.memoryManager.getMemoriesForSession();

			const eventMemory: SessionUpdateEvent = {
				type: 'session.update',
				session: {
					instructions: `You have the following memories: ${memories}`,
				},
			};

			this.eventManager.sendData(eventMemory);
		}
	}

	private checkInstructions(): void {
		if (!this.instructions) {
			this.getInstructions().subscribe(instructions => {
				this.instructions = instructions;
			});
		}
	}

	private addInstruction(instructionName: string): void {
		if (!this.instructionsAdded[instructionName as keyof InstructionResponse]) {
			this.instructionsAdded[instructionName as keyof InstructionResponse] = 'added';
			const instruction = this.instructions[instructionName as keyof InstructionResponse];

			const eventInstruction: SessionUpdateEvent = {
				type: 'session.update',
				session: {
					instructions: instruction,
				},
			};

			this.eventManager.sendData(eventInstruction);
			this.getResponse();
		}
	}

	private getResponse(message?: string): void {
		const eventResponse: ResponseCreateEvent = {
			type: 'response.create',
			response: {
				modalities: ['audio', 'text'],
				instructions: message + '\n\nYOU MUST ANSWER IN ENGLISH ONLY.',
			},
		};

		this.eventManager.sendData(eventResponse);
	}

	// TODO EFF-13458 - remove 'any' type
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private createRealtimeSession(): Observable<any> {
		return this.apollo
			.mutate({
				mutation: CREATE_REALTIME_SESSION,
				context: {
					client: AuthClient.Catalog,
				},
			})
			.pipe(
				map(({ data }) => (data?.createRealtimeSession ? JSON.parse(data.createRealtimeSession) : null)),
				take(1),
			);
	}

	private getInstructions(): Observable<InstructionResponse> {
		return this.apollo
			.query({
				query: GET_INSTRUCTIONS,
				context: {
					client: AuthClient.Catalog,
				},
			})
			.pipe(
				map(({ data }) => data.getInstructions),
				take(1),
			);
	}
}
